// Vuex module for the Account Management feature (account-management-port).
// Follows the repo's { state, mutations } convention (see datasetFilters.js / reportFilters.js).
// Holds:
//  - company-list filter/search/page state (R4.6, Property 3: round-trips through URL + store)
//  - the current company being viewed on Company Details (R7)
//  - the aggregated role permissions recomputed after a successful company update (R7.12)
//  - the Users-tab search/sort/page state (R9)

// Status filter values for the Company List (R4.3). 'All' is the unfiltered default.
const COMPANY_STATUS = {
  ALL: 'All',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

const initialState = {
  // --- Company List (R4): persisted to the URL and restored on return (R4.6) ---
  companyList: {
    searchTerm: '',
    // Default to showing only Active companies (R4.3).
    status: COMPANY_STATUS.ACTIVE,
    page: 1,
  },

  // --- Company Details (R7): the loaded company and its derived permission view ---
  currentCompany: null,
  // Aggregated permissions collected across the company's roles
  // (collectAllRolePermissions); recomputed after a successful updateCompany (R7.12).
  aggregatedPermissions: [],

  // --- User Details: the loaded user, used by the shell header to show a
  // route-aware title (e.g. the user's email). Cleared when leaving the screen.
  currentUser: null,

  // --- Users tab (R9): searchable/sortable/paginated user table state ---
  usersTab: {
    searchTerm: '',
    sortBy: '',
    sortDesc: false,
    page: 1,
  },
};

export default {
  state: Object.assign({}, initialState, {
    // nested objects need their own fresh copies so the module can be re-registered cleanly
    companyList: Object.assign({}, initialState.companyList),
    usersTab: Object.assign({}, initialState.usersTab),
  }),
  mutations: {
    // --- Company List ---
    setAccountCompanyListSearchTerm: (state, searchTerm) => {
      state.companyList.searchTerm = searchTerm;
    },
    setAccountCompanyListStatus: (state, status) => {
      state.companyList.status = status;
    },
    setAccountCompanyListPage: (state, page) => {
      state.companyList.page = page;
    },
    // Bulk-set the list state (used when restoring from the URL query — Property 3).
    setAccountCompanyListState: (state, listState) => {
      state.companyList = Object.assign({}, state.companyList, listState);
    },
    resetAccountCompanyListState: (state) => {
      state.companyList = Object.assign({}, initialState.companyList);
    },

    // --- Current company + aggregated permissions ---
    setAccountCurrentCompany: (state, company) => {
      state.currentCompany = company;
    },
    setAccountAggregatedPermissions: (state, permissions) => {
      state.aggregatedPermissions = permissions || [];
    },
    clearAccountCurrentCompany: (state) => {
      state.currentCompany = null;
      state.aggregatedPermissions = [];
    },

    // --- Current user (User Details) ---
    setAccountCurrentUser: (state, user) => {
      state.currentUser = user;
    },
    clearAccountCurrentUser: (state) => {
      state.currentUser = null;
    },

    // --- Users tab ---
    setAccountUsersSearchTerm: (state, searchTerm) => {
      // Searching resets to the first page (R9.2).
      state.usersTab.searchTerm = searchTerm;
      state.usersTab.page = 1;
    },
    setAccountUsersSort: (state, { sortBy, sortDesc }) => {
      state.usersTab.sortBy = sortBy;
      state.usersTab.sortDesc = sortDesc;
    },
    setAccountUsersPage: (state, page) => {
      state.usersTab.page = page;
    },
    resetAccountUsersTabState: (state) => {
      state.usersTab = Object.assign({}, initialState.usersTab);
    },
  },
};

export { COMPANY_STATUS };
